import { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  Clock,
  Mail,
  MessageSquare,
  Timer,
  UserCheck,
  UserMinus,
  UserPlus,
} from 'lucide-react';

import { TemplateComposer } from '@/components/contacts/TemplateComposer';
import { Button } from '@/components/ui/button';
import {
  useClaimConversation,
  useConversation,
  useHandbackConversation,
  useMarkAsRead,
  useSendImage,
  useSendMessage,
  useSendTemplate,
  useSetAutoHandback,
  useTakeoverConversation,
  useUnclaimConversation,
} from '@/hooks/useMessaging';
import { useCallEligibility } from '@/hooks/useOperatorCall';
import { cn } from '@/lib/utils';
import { MessageInput } from '@/pages/messaging/components/MessageInput';
import { useAuthStore } from '@/stores/auth.store';

import type { ContactConversationSummary } from '@repo/shared-types';

const REPLY_CHANNELS = ['WHATSAPP', 'EMAIL'] as const;
type ReplyChannel = (typeof REPLY_CHANNELS)[number];

const CHANNEL_META: Record<ReplyChannel, { label: string; icon: typeof MessageSquare }> = {
  WHATSAPP: { label: 'WhatsApp', icon: MessageSquare },
  EMAIL: { label: 'Email', icon: Mail },
};

/** Format a millisecond duration into a human-readable countdown string. */
const formatCountdown = (ms: number): string => {
  if (ms <= 0) return 'Expired';
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  return `${minutes}m remaining`;
};

/**
 * Inline reply box for the contact timeline. Mirrors the messaging inbox:
 * WhatsApp (text, templates, images, AI takeover) and email (text) — replying
 * into the contact's most recent thread per channel.
 */
export const ContactComposer = ({
  conversations,
  contactId,
  configurationId,
}: {
  conversations: ContactConversationSummary[];
  contactId?: string;
  configurationId?: string;
}) => {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const claim = useClaimConversation();
  const unclaim = useUnclaimConversation();
  const setAutoHandback = useSetAutoHandback();

  // Conversations arrive ordered by last_message_at desc — first per channel wins.
  const latestByChannel = useMemo(() => {
    const map = new Map<ReplyChannel, ContactConversationSummary>();
    for (const conv of conversations) {
      const channel = conv.channel as ReplyChannel;
      if (REPLY_CHANNELS.includes(channel) && !map.has(channel)) map.set(channel, conv);
    }
    return map;
  }, [conversations]);

  const available = REPLY_CHANNELS.filter((c) => latestByChannel.has(c));
  const [selected, setSelected] = useState<ReplyChannel | null>(null);
  const channel = selected && latestByChannel.has(selected) ? selected : (available[0] ?? null);
  const activeConv = channel ? latestByChannel.get(channel) : undefined;

  // Eligibility check for WhatsApp 24h window
  const { data: eligibility } = useCallEligibility(contactId, configurationId);
  const mustUseTemplate = channel === 'WHATSAPP' && eligibility?.mustUseTemplate === true;
  const whatsappWindowExpiresAt = eligibility?.whatsappWindowExpiresAt ?? null;

  // Countdown timer for the WhatsApp 24h window
  const [countdown, setCountdown] = useState<string | null>(null);

  useEffect(() => {
    if (channel !== 'WHATSAPP' || !whatsappWindowExpiresAt || mustUseTemplate) {
      setCountdown(null);
      return;
    }

    const update = () => {
      const remaining = new Date(whatsappWindowExpiresAt).getTime() - Date.now();
      if (remaining <= 0) {
        setCountdown(null);
        // Re-check eligibility when window expires
        qc.invalidateQueries({ queryKey: ['operator-calls', 'eligibility'] });
      } else {
        setCountdown(formatCountdown(remaining));
      }
    };

    update();
    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [channel, whatsappWindowExpiresAt, mustUseTemplate, qc]);

  const { data: conversation } = useConversation(activeConv?.id);
  const sendMessage = useSendMessage();
  const sendTemplate = useSendTemplate();
  const sendImage = useSendImage();
  const markAsRead = useMarkAsRead();
  const takeover = useTakeoverConversation();
  const handback = useHandbackConversation();
  const markedReadRef = useRef<string | null>(null);

  const refreshContacts = () => qc.invalidateQueries({ queryKey: ['contacts'] });

  useEffect(() => {
    if (
      activeConv?.id &&
      conversation &&
      conversation.unread_count > 0 &&
      markedReadRef.current !== activeConv.id
    ) {
      markedReadRef.current = activeConv.id;
      markAsRead.mutate(activeConv.id, { onSuccess: refreshContacts });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConv?.id, conversation?.unread_count]);

  if (!activeConv || !channel) {
    return (
      <div className="text-muted-foreground border-t px-4 py-3 text-xs">
        No WhatsApp or email thread yet — replying opens up once this contact messages in.
      </div>
    );
  }

  const conversationId = activeConv.id;
  const convConfigId = activeConv.configuration_id ?? configurationId ?? '';
  const isAiPaused = (conversation?.metadata as Record<string, unknown> | null)?.ai_paused === true;
  const sending = sendMessage.isPending || sendTemplate.isPending || sendImage.isPending;
  const assignedUserId = conversation?.assigned_user_id ?? null;
  const assignedUser = conversation?.assigned_user ?? null;
  const isClaimedByMe = assignedUserId === currentUser?.id;
  const autoHandbackMinutes = (conversation?.metadata as Record<string, unknown> | null)
    ?.auto_handback_minutes as number | undefined;

  const handleTemplateSent = () => {
    qc.invalidateQueries({ queryKey: ['operator-calls', 'eligibility'] });
    refreshContacts();
  };

  // Minutes after the last staff message before the AI takes the chat back.
  // No saved value means the server default (5 min); 0 means never.
  const HANDBACK_OPTIONS = [
    { label: '5 min', value: 5 },
    { label: '15 min', value: 15 },
    { label: '30 min', value: 30 },
    { label: '1 hour', value: 60 },
    { label: '2 hours', value: 120 },
    { label: 'Never', value: 0 },
  ] as const;

  return (
    <div className="bg-background border-t">
      <div className="flex items-center justify-between gap-2 px-3 pt-2">
        <div className="flex items-center gap-2">
          <div className="bg-muted/60 flex items-center gap-0.5 rounded-lg p-0.5">
            {available.map((c) => {
              const Meta = CHANNEL_META[c];
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setSelected(c)}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-150',
                    channel === c
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Meta.icon className="h-3.5 w-3.5" />
                  {Meta.label}
                </button>
              );
            })}
          </div>
          {countdown && (
            <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
              <Timer className="h-3 w-3" />
              Window: {countdown}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {assignedUser && !isClaimedByMe && (
            <span className="text-muted-foreground text-[10px]">{assignedUser.name}</span>
          )}
          <Button
            variant={isClaimedByMe ? 'secondary' : 'outline'}
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() =>
              isClaimedByMe
                ? unclaim.mutate(conversationId, { onSuccess: refreshContacts })
                : claim.mutate({ conversationId, contactId }, { onSuccess: refreshContacts })
            }
            disabled={claim.isPending || unclaim.isPending}
          >
            {isClaimedByMe ? (
              <>
                <UserMinus className="h-3.5 w-3.5" />
                Unclaim
              </>
            ) : (
              <>
                <UserPlus className="h-3.5 w-3.5" />
                Claim
              </>
            )}
          </Button>
          <Button
            variant={isAiPaused ? 'default' : 'outline'}
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() =>
              isAiPaused ? handback.mutate(conversationId) : takeover.mutate(conversationId)
            }
            disabled={takeover.isPending || handback.isPending}
          >
            {isAiPaused ? (
              <>
                <Bot className="h-3.5 w-3.5" />
                Return to AI
              </>
            ) : (
              <>
                <UserCheck className="h-3.5 w-3.5" />
                Take Over
              </>
            )}
          </Button>
          {isAiPaused && (
            <div className="relative">
              <select
                className="text-muted-foreground bg-muted/50 h-7 cursor-pointer appearance-none rounded-md border-0 py-0 pr-6 pl-2 text-xs focus:ring-2"
                value={autoHandbackMinutes ?? 5}
                onChange={(e) => {
                  setAutoHandback.mutate({ conversationId, minutes: Number(e.target.value) });
                }}
                title="Return to AI after this long with no staff message"
              >
                {HANDBACK_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Clock className="text-muted-foreground pointer-events-none absolute top-1/2 right-1.5 h-3 w-3 -translate-y-1/2" />
            </div>
          )}
        </div>
      </div>
      {mustUseTemplate ? (
        <TemplateComposer
          conversationId={conversationId}
          configurationId={convConfigId}
          onSent={handleTemplateSent}
        />
      ) : (
        <MessageInput
          onSend={(content) =>
            sendMessage.mutate({ conversationId, content }, { onSuccess: refreshContacts })
          }
          onSendTemplate={
            channel === 'WHATSAPP'
              ? (templateName, language, components, renderedText) =>
                  sendTemplate.mutate(
                    { conversationId, templateName, language, components, renderedText },
                    { onSuccess: refreshContacts },
                  )
              : undefined
          }
          onSendImage={
            channel === 'WHATSAPP'
              ? (file) => sendImage.mutate({ conversationId, file }, { onSuccess: refreshContacts })
              : undefined
          }
          disabled={sending}
          channel={channel}
        />
      )}
    </div>
  );
};
